import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { AuthConfig, getAuthConfig, waitForAuthConfig } from './authConfig';
import { environment } from '@web/config';
import {
  handleLoginFlow,
  getAccessToken,
  getCurrentUser,
  isTokenExpired,
  refreshAccessToken,
  logout as performLogout,
  stopAutoRefresh,
  unregisterBeforeUnload
} from './authService';

interface AuthState {
  authenticated: boolean;
  loading: boolean;
  token: string | null;
  user: any | null;
  isInternal: boolean;
  config: AuthConfig | null;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: () => Promise<void>;
  logout: (logoutType?: string) => Promise<void>;
  getToken: () => string | null;
  refreshToken: () => Promise<string>;
  hasRole: (role: string) => boolean;
  hasUserPersona: (userType: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const QUALTRICS_INIT_FLAG = '__UHCI_QUALTRICS_INITIALIZED__';

function getInitialAuthState(): AuthState {
  const config = getAuthConfig();
  const hasAuthCode = new URLSearchParams(window.location.search).has('code');
  const isLoggedOut = sessionStorage.getItem('logout') === 'true';
  const token = getAccessToken(config);
  const user = getCurrentUser();
  const userData = Array.isArray(user) ? user[0] : user;
  const hasUsableSession = Boolean(
    !hasAuthCode &&
    !isLoggedOut &&
    token &&
    user &&
    (config.isInternal ? !isTokenExpired(token) : userData?.AccessToken)
  );

  return {
    authenticated: hasUsableSession,
    loading: !hasUsableSession,
    token: hasUsableSession ? token : null,
    user: hasUsableSession ? user : null,
    isInternal: config.isInternal,
    config: hasUsableSession ? config : null,
    error: null
  };
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

/**
 * AuthProvider — React owns the full auth lifecycle.
 *
 * On mount:
 *   1. Resolves config from authConfig (VITE_* + defaults)
 *   2. Checks existing session (cookies/sessionStorage)
 *   3. If auth code in URL → exchanges for tokens → fetches user details
 *   4. If no session → redirects to IdP
 *   5. Starts auto-refresh
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(getInitialAuthState);

  const configRef = useRef<AuthConfig | null>(null);

  // Bootstrap auth on mount
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const hasAuthCode = new URLSearchParams(window.location.search).has('code');
        const config = await waitForAuthConfig();
        console.log('[React Auth] Config loaded:', config);
        if (cancelled) return;
        configRef.current = config;

        const result = await handleLoginFlow(config);
        if (cancelled) return;

        if (result) {
          // After successful auth-code callback, navigate to the landing page BEFORE
          // setting authenticated=true. This prevents React from briefly rendering
          // <AppShell> (top nav flash for ~1 frame) before the replace fires.
          // The page will reload — setState here would be discarded anyway.
          if (hasAuthCode) {
            const targetPath = config.isInternal ? '/myhome' : '/myinsights';
            if (window.location.pathname !== targetPath || window.location.search) {
              window.location.replace(targetPath);
              return; // Page will reload — no further processing needed
            }
          }

          setState({
            authenticated: true,
            loading: false,
            token: result.token,
            user: result.user,
            isInternal: config.isInternal,
            config,
            error: null
          });

          // Initialise Qualtrics SIE intercept — mirrors Angular app.component.ts startUserAnalytics().
          // userAnalytics() is defined in /user-review.js loaded as a global script.
          try {
            if (
              environment.qualtrics &&
              typeof (window as any).userAnalytics === 'function' &&
              !(window as any)[QUALTRICS_INIT_FLAG]
            ) {
              (window as any)[QUALTRICS_INIT_FLAG] = true;
              (window as any).userAnalytics(environment.qualtricksEnvCheck);
            }
          } catch {
            // Non-critical — Qualtrics failure should never break the app
          }
        } else {
          // Login redirect initiated or user just logged out
          setState((prev: AuthState) => ({
            ...prev,
            loading: false,
            config,
            isInternal: config.isInternal
          }));
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('[React Auth] Bootstrap error:', err);
        setState((prev: AuthState) => ({
          ...prev,
          loading: false,
          error: err?.message || 'Authentication failed'
        }));
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
      stopAutoRefresh();
      unregisterBeforeUnload();
    };
  }, []);

  const login = useCallback(async () => {
    const config = configRef.current;
    if (!config) return;
    sessionStorage.removeItem('logout');
    const result = await handleLoginFlow(config);
    if (result) {
      setState((prev: AuthState) => ({
        ...prev,
        authenticated: true,
        loading: false,
        token: result.token,
        user: result.user,
        isInternal: config.isInternal,
        config,
        error: null
      }));
    }
  }, []);

  const doLogout = useCallback(async (logoutType: string = 'user') => {
    const config = configRef.current;
    if (!config) return;
    await performLogout(config, logoutType);
    // Navigate BEFORE setState to prevent React from re-rendering with authenticated=false
    // (which briefly flashes the SignedOut / loading screen before the page unloads).
    // Internal users: replace to root — page unloads so setState is never needed.
    // External users: performLogout already called window.location.href inside, page is unloading.
    if (config.isInternal) {
      window.location.replace('/');
      return; // Page is unloading — no further React state updates needed
    }
    // External: page is already navigating away (OHID logout redirect fired in performLogout).
    // Only update state if somehow we're still here (edge case: no logoutRedirectUrl configured).
    setState((prev: AuthState) => ({
      ...prev,
      authenticated: false,
      token: null,
      user: null,
      loading: false,
      error: null
    }));
  }, []);

  // Listen for parcel header 'signout' CustomEvent (parcel dispatches this on sign-out click)
  useEffect(() => {
    const handleSignout = () => {
      doLogout();
    };
    window.addEventListener('signout', handleSignout);
    return () => window.removeEventListener('signout', handleSignout);
  }, [doLogout]);

  const getToken = useCallback((): string | null => {
    const config = configRef.current;
    if (!config) return null;
    return getAccessToken(config);
  }, []);

  const doRefreshToken = useCallback(async (): Promise<string> => {
    const config = configRef.current;
    if (!config) throw new Error('Config not available');
    const token = await refreshAccessToken(config);
    setState((prev: AuthState) => ({
      ...prev,
      token,
      authenticated: !!token
    }));
    return token;
  }, []);

  const hasRole = useCallback(
    (role: string): boolean => {
      if (!state.user) return false;
      const userData = Array.isArray(state.user) ? state.user[0] : state.user;
      const personas = userData?.UserPersonas || [];
      const roles: string[] = personas.map((p: any) => p?.UserRole || p?.UserType).filter(Boolean);
      const directRoles: string[] = userData?.roles || userData?.Roles || [];
      return [...roles, ...directRoles].includes(role);
    },
    [state.user]
  );

  const hasUserPersona = useCallback(
    (userType: string): boolean => {
      if (!state.user) return false;
      const userData = Array.isArray(state.user) ? state.user[0] : state.user;
      const personas = userData?.UserPersonas || [];
      return personas.some((p: any) => p?.UserType === userType);
    },
    [state.user]
  );

  const value: AuthContextValue = {
    ...state,
    login,
    logout: doLogout,
    getToken,
    refreshToken: doRefreshToken,
    hasRole,
    hasUserPersona
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
